import React, { useEffect, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Search, X } from 'lucide-react';

/**
 * SearchInput — shared, debounced search input used across every search bar
 * in the app (admin panels, CMS, moderator queue, etc).
 *
 * Behavior contract (see Review.pdf #8):
 *  1. Debounces user input by `debounceMs` (default 300ms) before firing
 *     `onDebouncedChange`. This makes typing feel live without hammering the
 *     backend on every keystroke.
 *  2. Renders a clear (×) button whenever text is present. Clicking it wipes
 *     the field AND fires `onDebouncedChange('')` immediately (no debounce
 *     wait) so the underlying table refreshes to the unfiltered list.
 *  3. Pressing `Enter` submits the current value immediately, bypassing the
 *     remaining debounce window — useful for keyboard-heavy admins.
 *  4. Never renders a blank page on empty — the caller decides how to react
 *     to `''`, but we guarantee that the empty callback fires.
 *
 * Uncontrolled by default: the component owns the input state so parents
 * only need to react to debounced changes. Pass `value` + `onChange` for the
 * controlled variant.
 */
export const SearchInput = ({
  value: controlledValue,
  onChange,               // per-keystroke change (optional, controlled mode)
  onDebouncedChange,      // fires after debounceMs of quiet, or on clear/Enter
  onSubmit,               // fires on Enter key
  placeholder = 'Search…',
  debounceMs = 300,
  autoFocus = false,
  className = '',
  disabled = false,
  testid = 'search-input',
}) => {
  const isControlled = controlledValue !== undefined;
  const [internal, setInternal] = useState(controlledValue || '');
  const current = isControlled ? controlledValue : internal;
  const timerRef = useRef(null);
  const lastFiredRef = useRef(current);
  const debouncedRef = useRef(onDebouncedChange);
  debouncedRef.current = onDebouncedChange;

  // Keep internal state in sync when parent flips controlled value
  useEffect(() => {
    if (isControlled) setInternal(controlledValue || '');
  }, [controlledValue, isControlled]);

  const scheduleFire = (next) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (lastFiredRef.current !== next) {
        lastFiredRef.current = next;
        debouncedRef.current && debouncedRef.current(next);
      }
    }, debounceMs);
  };

  const fireNow = (next) => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (lastFiredRef.current !== next) {
      lastFiredRef.current = next;
      debouncedRef.current && debouncedRef.current(next);
    }
  };

  useEffect(() => () => timerRef.current && clearTimeout(timerRef.current), []);

  const handleChange = (e) => {
    const next = e.target.value;
    if (!isControlled) setInternal(next);
    onChange && onChange(next);
    if (next === '') fireNow(''); else scheduleFire(next);
  };

  const clear = () => {
    if (!isControlled) setInternal('');
    onChange && onChange('');
    fireNow('');
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      fireNow(current);
      onSubmit && onSubmit(current);
    } else if (e.key === 'Escape' && current) {
      e.stopPropagation();
      clear();
    }
  };

  return (
    <div className={`relative flex-1 ${className}`}>
      <Search className='absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none' />
      <Input
        className='pl-8 pr-8'
        placeholder={placeholder}
        value={current}
        onChange={handleChange}
        onKeyDown={onKeyDown}
        disabled={disabled}
        autoFocus={autoFocus}
        data-testid={testid}
      />
      {current && (
        <button
          type='button'
          onClick={clear}
          className='absolute right-2 top-1/2 -translate-y-1/2 h-5 w-5 rounded-full text-muted-foreground hover:bg-secondary hover:text-foreground inline-flex items-center justify-center'
          aria-label='Clear search'
          data-testid={`${testid}-clear`}
        >
          <X className='h-3.5 w-3.5' />
        </button>
      )}
    </div>
  );
};

export default SearchInput;
